import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  styled,
} from '@mui/material';
import { PrimaryButton, SecondaryButton } from '../components';
import InfoHeader from '../components/InfoHeader';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import BasicMenu from '../components/BasicMenu';
import { AmbienteInternoPontoFraco } from '../types/negocio/AmbienteInternoPontoFraco';
import { ambienteInternoPontoFracoService } from '../services/AmbienteInternoPontoFracoService';
import toastService from '../utils/toastService';
import SaveStatusIndicator, { SaveStatus } from '../components/SaveStatusIndicator';
import LoadingIndicator from '../components/LoadingIndicator';
import InfoPopover from '../components/InfoPopover';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#042E4B',
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const PontosFracos: React.FC = () => {
  const [rows, setRows] = useState<AmbienteInternoPontoFraco[]>([]);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [loading, setLoading] = useState<boolean>(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await ambienteInternoPontoFracoService.listarTodos();
        setRows(data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toastService.error('Erro ao carregar pontos fracos');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = async (index: number, field: string, value: string | number, save: boolean = true) => {
    const updatedRows = [...rows];
    (updatedRows[index] as any)[field] = value;
    updatedRows[index].pontuacao = updatedRows[index].gravidade * updatedRows[index].urgencia * updatedRows[index].tendencia;
    if (save) await salvar(updatedRows[index]);
    setRows(updatedRows);
  };

  const handleAddRow = async () => {
    const novo: AmbienteInternoPontoFraco = {
      descricao: '',
      gravidade: 1,
      urgencia: 1,
      tendencia: 1,
      pontuacao: 1,
      acoes: '',
      ordem: rows.length + 1,
    };
    const newRows = [...rows, novo];
    setRows(newRows);

    const newIndex = newRows.length - 1;
    setEditingRow(newIndex);

    setTimeout(() => {
      const el = rowRefs.current[newIndex];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  async function salvar(ambienteInternoPontoForte: AmbienteInternoPontoFraco) {
    setSaveStatus('saving');
    try {
      await ambienteInternoPontoFracoService.save(ambienteInternoPontoForte);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error saving row:', error);
      setSaveStatus('error');
      toastService.error('Erro ao salvar ponto fraco');
    }
  }

  const handleDeleteRow = async (index: number) => {
    setSaveStatus('saving');
    try {
      if (rows[index].guid) await ambienteInternoPontoFracoService.deletar(rows[index].guid!);
      const newRows = [...rows];
      newRows.splice(index, 1);
      newRows.forEach((row, i) => (row.ordem = i + 1));
      await ambienteInternoPontoFracoService.salvarTodos(newRows);
      setRows(newRows);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error deleting row:', error);
      setSaveStatus('error');
      toastService.error('Erro ao deletar ponto fraco');
    }
  };

  const handleDeleteAll = async () => {
    setOpenConfirmDialog(false);
    setSaveStatus('saving');
    try {
      // falta criar endpoint de deletarTodos
      // await ambienteInternoPontoForteService.deletarTodos();
      setRows([]);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error deleting all rows:', error);
      setSaveStatus('error');
      toastService.error('Erro ao excluir todos os Pontos Fracos');
    }
  };

  return (
    <Box>
      <InfoHeader
        title="Pontos Fracos"
        message="Avalie os pontos fracos de sua empresa. Classifique gravidade, urgência e tendência para cada item."
        isImportant
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2 }}>
          <Tooltip title="Adicionar um novo Ponto Fraco">
            <PrimaryButton onClick={handleAddRow}>
              + Adicionar
            </PrimaryButton>
          </Tooltip>
          <Tooltip title="Excluir todos os Pontos Fracos">
            <PrimaryButton variant="outlined" color="error" onClick={() => setOpenConfirmDialog(true)}>
              Excluir Tudo
            </PrimaryButton>
          </Tooltip>
        </Box>
        <SaveStatusIndicator status={saveStatus} />
      </Box>

      <Grid container sx={{ mt: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Paper elevation={3}>
            <TableContainer style={{ borderRadius: '8px' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell width="54%"><strong>Ponto Fraco</strong></StyledTableCell>
                    <StyledTableCell align="center" width="12%">
                      <InfoPopover title="Gravidade" description="Consequência caso não resolvido." placement="top"><strong>Gravidade</strong></InfoPopover>
                    </StyledTableCell>
                    <StyledTableCell align="center" width="12%">
                      <InfoPopover title="Urgência" description="Necessidade de tempo para resolução." placement="top"><strong>Urgência</strong></InfoPopover>
                    </StyledTableCell>
                    <StyledTableCell align="center" width="12%">
                      <InfoPopover title="Tendência" description="Evolução ao longo do tempo." placement="top"><strong>Tendência</strong></InfoPopover>
                    </StyledTableCell>
                    <StyledTableCell align="center" width="5%"><strong>Pontuação</strong></StyledTableCell>
                    <StyledTableCell align="center" width="5%"><strong>Ações</strong></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6}><LoadingIndicator message="Carregando Pontos Fracos..." /></TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}><Typography>Nenhum ponto fraco encontrado.</Typography></TableCell></TableRow>
                  ) : rows.map((row, index) => (
                    <StyledTableRow key={index} ref={(el) => { rowRefs.current[index] = el }}>
                      <StyledTableCell>
                        {editingRow === index ? (
                          <>
                            <TextField
                              fullWidth
                              multiline
                              minRows={1}
                              variant="standard"
                              autoFocus
                              value={row.descricao}
                              onChange={(e) => handleChange(index, 'descricao', e.target.value, false)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (row.descricao.trim() !== '') {
                                    salvar(row);
                                    setEditingRow(null);
                                  } else {
                                    toastService.warning("A descrição não pode estar vazia.");
                                  }
                                }
                              }}
                              onBlur={() => {
                                if (row.descricao.trim() !== '') {
                                  salvar(row);
                                  setEditingRow(null);
                                } else {
                                  toastService.warning("A descrição não pode estar vazia.");
                                  const newRows = [...rows];
                                  newRows.splice(index, 1);
                                  setRows(newRows);
                                  setEditingRow(null);
                                }
                              }}
                              sx={{ '& .MuiInputBase-input': { fontSize: 16, cursor: 'text', padding: '4px 0px' } }}
                            />
                            <Typography sx={{ color: 'gray', fontSize: '0.75rem', mt: 1 }}>
                              Pressione "Enter" ou clique fora para confirmar
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: 16 }}>{row.descricao}</Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Select value={row.gravidade} onChange={(e) => handleChange(index, 'gravidade', e.target.value)} size="small">
                          {[1, 2, 3, 4].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                        </Select>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Select value={row.urgencia} onChange={(e) => handleChange(index, 'urgencia', e.target.value)} size="small">
                          {[1, 2, 3, 4].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                        </Select>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Select value={row.tendencia} onChange={(e) => handleChange(index, 'tendencia', e.target.value)} size="small">
                          {[1, 2, 3, 4].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                        </Select>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Typography variant="body2">{row.pontuacao}</Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <BasicMenu onEdit={() => setEditingRow(index)} onDelete={() => handleDeleteRow(index)} />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza de que deseja excluir todos os pontos fracos? Esta ação não poderá ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton variant="contained" onClick={() => setOpenConfirmDialog(false)}>Cancelar</SecondaryButton>
          <PrimaryButton color="error" variant="contained" onClick={handleDeleteAll}>Confirmar</PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PontosFracos;
